import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { X, ShoppingBag, Trash2, Tag, ArrowRight, ShieldCheck, Lock, Check, Zap, Sparkles } from "lucide-react";
import { useBasket } from "../context/BasketContext";
import ReservationSuccessModal from "./ReservationSuccessModal";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_IMAGES = {
  // SmartSite tiers
  "smartsite-lite": "/basket_lite.png",
  "smartsite-rebuild": "/basket_rebuild.png",
  "smartsite-premium": "/basket_premium.png",
  "smartsite-pro": "/basket_pro.png",
  // SmartOS
  "smartos-desktop": "/basket_pro.png",
  "smartos-app": "/basket_premium.png",
  // Bundles
  "bundle-starter": "/basket_lite.png",
  "bundle-conversion": "/basket_rebuild.png",
  "bundle-automation": "/basket_premium.png",
  "bundle-infrastructure": "/basket_pro.png",
  "bundle-scale": "/basket_pro.png",
  // Add-ons
  "addon-smart-lead": "/basket_rebuild.png",
  "addon-whatsapp": "/basket_lite.png",
  "addon-google-trust": "/basket_lite.png",
  "addon-conversion": "/basket_rebuild.png",
  "addon-booking": "/basket_rebuild.png",
  "addon-ai-frontdesk": "/basket_premium.png",
  "addon-domain-rescue": "/basket_lite.png",
  "addon-brand-kit": "/basket_lite.png",
  "addon-mini-playbook": "/basket_lite.png",
  "addon-ops-playbook": "/basket_rebuild.png",
  "addon-full-blueprint": "/basket_premium.png",
  "addon-mvp-app": "/basket_pro.png",
};

export default function BasketDrawer() {
  const {
    items, removeItem, isOpen, setIsOpen, itemCount,
    subtotal, discount, total, promoCode, promoDiscount, promoValid, applyPromo, clearBasket,
  } = useBasket();
  const [promoInput, setPromoInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [addedCarePlan, setAddedCarePlan] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [reservation, setReservation] = useState(null);

  // Check if basket has a tier product (to show care plan upsell)
  const hasTier = items.some((i) =>
    ["smartsite-lite", "smartsite-rebuild", "smartsite-premium", "smartsite-pro"].includes(i.product_id)
  );

  // Try to derive the headline tier from basket contents (best effort)
  const detectedTier = useMemo(() => {
    if (!items.length) return null;
    const text = items.map((i) => (i.name || "").toUpperCase()).join(" ");
    if (text.includes("ELITE")) return "ELITE";
    if (text.includes("PRO")) return "PRO";
    if (text.includes("GROWTH")) return "GROWTH";
    if (text.includes("CORE")) return "CORE";
    return null;
  }, [items]);

  const handleCheckout = async () => {
    setCheckoutError("");
    if (!name.trim()) { setCheckoutError("Please enter your name."); return; }
    if (!email.trim()) { setCheckoutError("Please enter your email."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/subscribe/reserve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || null,
          tier: detectedTier,
          items: items.map((i) => ({ name: i.name, quantity: i.quantity, price: i.price })),
          total: total ? `£${Number(total).toFixed(2)}` : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Reservation could not be created. Please try again or email aaron@stratiqsolutions.co.uk.");
      }
      const data = await res.json();
      setReservation(data);
      setIsOpen(false);
      // Clear basket once the reservation is locked in.
      try { clearBasket && clearBasket(); } catch (e) { /* noop */ }
    } catch (err) {
      setCheckoutError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const close = () => setIsOpen(false);

  return (
    <>
      {/* Backdrop - always mounted, visibility controlled by opacity + pointer-events */}
      <div
        className="fixed inset-0 bg-black/70 z-[70] transition-opacity duration-200"
        style={{
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
        onClick={close}
        data-testid="basket-overlay"
      />
      
      {/* Drawer - always mounted, position controlled by transform */}
      <div
        className="fixed right-0 top-0 bottom-0 w-full max-w-md z-[71] transition-transform duration-300 ease-out"
        style={{
          height: '100dvh',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
        data-testid="basket-drawer"
      >
        {/* Main container */}
        <div className="h-full bg-slate-950 border-l border-slate-800/50 flex flex-col" style={{ maxHeight: '100dvh' }}>

              {/* STRATIQ branded header */}
              <div className="relative px-5 py-4 border-b border-slate-800/60 bg-slate-950">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img src="/stratiq_official_logo_cropped.png" alt="STRATIQ" className="h-7 w-auto" />
                    <div className="h-5 w-px bg-slate-700" />
                    <div className="flex items-center gap-2">
                      <ShoppingBag size={15} className="text-slate-400" />
                      <span className="text-white font-semibold text-sm">
                        {itemCount} {itemCount === 1 ? 'item' : 'items'}
                      </span>
                    </div>
                  </div>
                  <button 
                    onClick={close} 
                    className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-all group" 
                    data-testid="basket-close"
                  >
                    <X size={16} className="text-slate-400 group-hover:text-white transition-colors" />
                  </button>
                </div>
              </div>

              {/* Scrollable content. items + form flow together */}
              <div className="flex-1 overflow-y-auto overscroll-contain min-h-0">
                {/* Items Section */}
                <div className="px-5 py-4">
                {items.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center">
                      <ShoppingBag size={24} className="text-slate-600" />
                    </div>
                    <p className="text-slate-400 font-medium mb-2">Your basket is empty</p>
                    <Link 
                      to="/pricing" 
                      onClick={() => setIsOpen(false)} 
                      className="text-blue-400 text-sm font-semibold hover:text-blue-300 transition-colors inline-flex items-center gap-1"
                    >
                      Browse packages <ArrowRight size={14} />
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {items.map((item, idx) => (
                      <div 
                        key={item.product_id}
                        className="group relative bg-slate-900/80 rounded-xl p-3.5 border border-slate-800/60 hover:border-slate-700 transition-all duration-200"
                        data-testid={`basket-item-${item.product_id}`}
                      >
                        <div className="relative flex items-start gap-3">
                          <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-slate-800 border border-slate-700/50 flex items-center justify-center">
                            {item.product_id?.startsWith("smartsite") ? (
                              <Zap size={15} className="text-blue-400" />
                            ) : (
                              <Sparkles size={15} className="text-blue-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-white text-sm leading-tight">{item.name}</p>
                            <p className="text-blue-400 font-bold text-sm mt-0.5">
                              £{item.price?.toLocaleString("en-GB", { minimumFractionDigits: 0 }) || '0'}
                            </p>
                          </div>
                          <button 
                            onClick={() => removeItem(item.product_id)} 
                            className="p-1.5 rounded-lg hover:bg-red-500/10 transition-all group/btn" 
                            data-testid={`remove-${item.product_id}`}
                          >
                            <Trash2 size={14} className="text-slate-500 group-hover/btn:text-red-400 transition-colors" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                </div>

                {/* Checkout form fields. inside scrollable area for flush layout */}
                {items.length > 0 && (
                  <div className="px-5 pb-4 space-y-3">
                    {/* Email + Phone */}
                    <div className="space-y-2.5">
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] mb-1 block">Email for confirmation</label>
                        <input
                          type="email"
                          placeholder="your@email.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full bg-slate-900 border border-slate-700/60 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-slate-500 transition-all outline-none"
                          data-testid="checkout-email-input"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] mb-1 block">Phone (optional)</label>
                        <input
                          type="tel"
                          placeholder="+44 7700 900000"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className="w-full bg-slate-900 border border-slate-700/60 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-slate-500 transition-all outline-none"
                          data-testid="checkout-phone-input"
                        />
                      </div>
                    </div>

                    {/* Care Plan Upsell */}
                    {hasTier && !addedCarePlan && (
                      <div className="bg-blue-950/40 border border-blue-800/30 rounded-lg p-3.5">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-500/15 border border-blue-500/25 flex items-center justify-center flex-shrink-0">
                            <ShieldCheck size={14} className="text-blue-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-white leading-tight">Protect your investment</p>
                            <p className="text-xs text-slate-400 mt-0.5 mb-1.5">
                              Add STRATIQ Growth from <span className="font-bold text-blue-400">£329/mo</span>
                            </p>
                            <button
                              onClick={() => setAddedCarePlan(true)}
                              className="text-xs font-bold text-blue-400 hover:text-blue-300 transition-colors inline-flex items-center gap-1"
                              data-testid="add-care-plan"
                            >
                              Add Care Plan <ArrowRight size={12} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {hasTier && addedCarePlan && (
                      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 flex items-center gap-2">
                        <Check size={14} className="text-emerald-400 flex-shrink-0" />
                        <p className="text-xs font-medium text-emerald-400">STRATIQ Care added. Plan confirmed after purchase.</p>
                      </div>
                    )}

                    {/* Promo Code */}
                    <div>
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                          <input
                            type="text"
                            placeholder="Promo code"
                            value={promoInput}
                            onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                            className="w-full pl-9 pr-3 py-2.5 bg-slate-900 border border-slate-700/60 focus:border-blue-500/50 rounded-lg text-sm text-white placeholder:text-slate-500 transition-all outline-none"
                            data-testid="promo-input"
                          />
                        </div>
                        <button
                          onClick={() => applyPromo(promoInput)}
                          className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 border border-slate-700/60 text-white text-xs font-bold rounded-lg transition-all"
                          data-testid="apply-promo"
                        >
                          Apply
                        </button>
                      </div>
                      {promoValid === true && (
                        <p className="text-emerald-400 text-xs font-semibold mt-2 flex items-center gap-1" data-testid="promo-success">
                          <Check size={12} /> {promoCode} applied. {promoDiscount}% off
                        </p>
                      )}
                      {promoValid === false && (
                        <p className="text-red-400 text-xs font-semibold mt-2" data-testid="promo-error">Invalid promo code</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Fixed bottom. Totals + Checkout only */}
              {items.length > 0 && (
                <div className="border-t border-slate-800/60 px-5 py-4 space-y-3 bg-slate-950 flex-shrink-0">
                  {/* Totals */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm text-slate-400">
                      <span>Subtotal</span>
                      <span className="text-slate-200">£{subtotal.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
                    </div>
                    {discount > 0 && (
                      <div className="flex justify-between text-sm text-emerald-400 font-medium" data-testid="basket-discount">
                        <span>Discount ({promoDiscount}%)</span>
                        <span>-£{discount.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-baseline pt-2 border-t border-slate-800/30">
                      <span className="text-sm font-medium text-slate-300">Total</span>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-white" data-testid="basket-total">
                          £{total.toLocaleString("en-GB", { minimumFractionDigits: 2 })}
                        </span>
                        <p className="text-[10px] text-slate-500 mt-0.5">One time payment</p>
                      </div>
                    </div>
                  </div>

                  {/* Reservation form */}
                  <div className="space-y-2 pt-1">
                    <input
                      type="text"
                      placeholder="Your full name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-3 py-2.5 bg-slate-900 border border-slate-700/60 focus:border-blue-500/50 rounded-lg text-sm text-white placeholder:text-slate-500 transition-all outline-none"
                      data-testid="reserve-name-input"
                    />
                    <input
                      type="email"
                      placeholder="Email for confirmation"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-3 py-2.5 bg-slate-900 border border-slate-700/60 focus:border-blue-500/50 rounded-lg text-sm text-white placeholder:text-slate-500 transition-all outline-none"
                      data-testid="reserve-email-input"
                    />
                    <input
                      type="tel"
                      placeholder="Phone (optional)"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full px-3 py-2.5 bg-slate-900 border border-slate-700/60 focus:border-blue-500/50 rounded-lg text-sm text-white placeholder:text-slate-500 transition-all outline-none"
                      data-testid="reserve-phone-input"
                    />
                  </div>

                  {/* Checkout Error */}
                  {checkoutError && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-400 font-medium" data-testid="checkout-error">
                      {checkoutError}
                    </div>
                  )}

                  {/* Reserve Button (no card, books onboarding call) */}
                  <button
                    onClick={handleCheckout}
                    disabled={loading}
                    className="relative w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold py-3.5 rounded-xl transition-all flex items-center justify-center gap-2 text-sm tracking-wide"
                    data-testid="checkout-button"
                  >
                    {loading ? (
                      <span className="animate-pulse">Reserving your slot...</span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Reserve my onboarding call <ArrowRight size={14} />
                      </span>
                    )}
                  </button>

                  {/* Trust Badges */}
                  <div className="flex items-center justify-center gap-4 pt-1">
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-500 font-medium">
                      <ShieldCheck size={12} className="text-emerald-500/70" /> Stripe Verified
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-500 font-medium">
                      <Lock size={12} className="text-blue-500/70" /> No card needed yet
                    </div>
                  </div>

                  <p className="text-center text-[10px] text-slate-600 leading-relaxed">
                    No card taken now. We book a 30 min onboarding call with Aaron, then send a Stripe verified payment link after the call.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Reservation success modal (lives outside the drawer) */}
          <ReservationSuccessModal
            open={!!reservation}
            onClose={() => setReservation(null)}
            data={reservation}
          />
      </>
  );
}
